import { logV } from "../../shared/services/debug-logger";
import { useAdminAgentConversations } from "../hooks/useAdminAgentConversations";
import type { AdminAgentPanelProps } from "../models/admin-agent.models";
import { ADMIN_AGENT_PANEL_ID, ADMIN_AGENT_STATUS_LABELS } from "../utils/admin-agent.constants";
import AdminAgentApproval from "./AdminAgentApproval";
import AdminAgentChatControls from "./AdminAgentChatControls";
import AdminAgentComposer from "./AdminAgentComposer";
import AdminAgentMessages from "./AdminAgentMessages";
import AdminAgentResizeHandle from "./AdminAgentResizeHandle";
import AdminAgentSettingsControls from "./AdminAgentSettingsControls";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";

// Own the chat lifecycle without adding state management to the admin route outlet.
// The backend filters history by the caller's identity and current access scope.
const AdminAgentPanel = ({ onClose, resize }: AdminAgentPanelProps) => {
  const chat = useAdminAgentConversations();
  const closeRef = useRef<HTMLButtonElement>(null);
  const conversation = chat.conversation;
  const busy = Boolean(chat.pendingAction);
  logV("AdminAgentPanel - BUILD", { loading: chat.loading, busy, status: conversation?.status });

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  return (
    <aside
      id={ADMIN_AGENT_PANEL_ID}
      aria-label="Admin assistant"
      data-test="admin-agent-panel"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onClose();
        }
      }}
      className="border-greyscale-20 absolute inset-y-0 right-0 z-40 flex w-[min(100vw,var(--admin-agent-panel-width))] flex-col border-l bg-white text-gray-900 shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 lg:w-full"
    >
      <AdminAgentResizeHandle resize={resize} />
      <header className="border-greyscale-20 flex shrink-0 items-center gap-2 border-b px-3 py-2 dark:border-gray-700">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">Admin assistant</h2>
        {conversation && (
          <span
            data-test="admin-agent-run-status"
            role="status"
            title={
              conversation.run
                ? `${conversation.run.stepCount}/${conversation.run.maxSteps} AI steps · ${conversation.lastActor}`
                : undefined
            }
            className={`shrink-0 text-[11px] ${conversation.status === "running" ? "font-semibold text-green dark:text-emerald-300" : "text-gray-500 dark:text-gray-400"}`}
          >
            {conversation.pendingApproval ? "Awaiting permission" : ADMIN_AGENT_STATUS_LABELS[conversation.status]}
          </span>
        )}
        <details className="relative shrink-0" data-test="admin-agent-chat-info">
          <summary
            aria-label="About admin chats"
            title="About admin chats"
            className="cursor-pointer list-none rounded p-1 text-gray-500 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:text-gray-400 dark:hover:bg-gray-800 [&::-webkit-details-marker]:hidden"
          >
            <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
          </summary>
          <p
            data-test="admin-agent-access-memory-notice"
            className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-3 text-xs leading-5 text-gray-600 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          >
            New signed-in chats are private to your current access. Local development can access the registered catalog.
            Every OGA agent call needs your permission and may incur additional costs. History clears when the backend
            restarts.
          </p>
        </details>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close admin assistant"
          data-test="admin-agent-close"
          className="rounded p-1 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:hover:bg-gray-800"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <AdminAgentChatControls
        conversations={chat.conversations}
        selectedId={chat.selectedId}
        conversation={conversation}
        busy={busy}
        onSelect={chat.selectConversation}
        onCreate={chat.createConversation}
        onRename={chat.renameConversation}
        onDelete={chat.removeConversation}
      />

      {conversation && (
        <AdminAgentSettingsControls conversation={conversation} busy={busy} onSettingsChange={chat.changeSettings} />
      )}

      {chat.error && (
        <div
          role="alert"
          data-test="admin-agent-error"
          className="shrink-0 border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
        >
          <p>{chat.error}</p>
          <button
            type="button"
            data-test="admin-agent-retry"
            onClick={() => void chat.refreshConversations()}
            className="mt-2 font-semibold underline"
          >
            Refresh chats
          </button>
        </div>
      )}

      <AdminAgentMessages conversation={conversation} loading={chat.loading} />

      {conversation?.pendingApproval && (
        <AdminAgentApproval approval={conversation.pendingApproval} busy={busy} onDecide={chat.decideApproval} />
      )}

      {conversation?.run?.error && (
        <p
          role="alert"
          data-test="admin-agent-run-error"
          className="shrink-0 px-3 py-2 text-xs text-red-700 dark:text-red-300"
        >
          {conversation.run.error}
        </p>
      )}

      <AdminAgentComposer
        conversation={conversation}
        draft={chat.draft}
        pendingAction={chat.pendingAction}
        onDraftChange={chat.updateDraft}
        onSend={chat.sendMessage}
        onStop={chat.stopConversation}
        onResume={chat.resumeConversation}
      />
    </aside>
  );
};

export default AdminAgentPanel;
